import React from "react";
import { Slide, Box, Container } from "@mui/material";
import MdViewer from "./mdViewer";
import ArticleNavigator from "./articleNavigator";
import ErrorModal from "../errorModal";
import { useLocation, useNavigate } from "react-router-dom";
import { useHasNavigated } from "../../context/hasNavigated";
import { useGpuTier } from "../../context/gpuTierProvider";
import useFocusTrapIn from "../../hooks/focusTrapIn";
import pages from "../pages";

// Things that potentially need fixing:
// fix lag when opening or closing navigator
// fix lag when making the focustrap slide in or out of view
// fix lag when the user switches articles
// fix the image viewer in landscape

const FocusTrap = () => {
  const { pathname: route } = useLocation(); // this context changes, triggers first re-render
  const hasNavigated = useHasNavigated();
  const gpuTier = useGpuTier();
  const navigate = useNavigate();
  const [content, setContent] = React.useState("");
  const [contentIdx, setContentIdx] = React.useState("");
  const [errorRetry, setErrorRetry] = React.useState(null);
  const startPointRef = React.useRef();
  const isIn = useFocusTrapIn();

  React.useEffect(() => {
    const page = pages.find((page) => page.route === route);

    if (!page) {
      navigate("/404");
      return;
    }

    if (!isIn) {
      document.title = "Confused Heathen - A Biography"
      return;
    };

    const pageIdx = pages.indexOf(page);
    setContentIdx(pageIdx); // this triggers the second re-render

    if (page.content) {
      document.title = `Confused Heathen - ${page.name}`;
      setContent(page.content);
      setErrorRetry(null);
      return;
    }

    const getContent = () =>
      page.getContent().then((newContent) => {
        // this triggers the third re-render
        document.title = `Confused Heathen - ${page.name}`;
        page.content = newContent;
        setContent(newContent);
        setErrorRetry(null);
      });

    getContent().catch((e) => {
      if (e.name === "ChunkLoadError") {
        setErrorRetry({
          getContent: () => getContent().catch(() => {}),
        });
      }
    });
  }, [navigate, isIn, route]);

  React.useEffect(() => {
    hasNavigated && startPointRef.current?.scrollIntoView();
  }, [contentIdx, hasNavigated]);

  const prevArticle = pages[contentIdx - 1];
  const nextArticle = pages[contentIdx + 1];

  return (
    <Slide direction="down" in={isIn} appear={hasNavigated}>
      <Box
        sx={{
          ...(gpuTier <= 1
            ? {
                background: "rgba(21, 17, 21, 1)",
              }
            : {
                background:
                  "linear-gradient(180deg, rgba(21, 27, 31, 0.75), rgba(21, 27, 31, 0.85))",
                backdropFilter: "blur(20px)",
              }),
          overflow: isIn ? "unset" : "hidden",
          transform: "translate3d(0, 0, 0)",
          width: "100%",
          flexGrow: 1,
          zIndex: 1000,
        }}
        component="article"
      >
        <div ref={startPointRef} />
        {errorRetry && (
          <ErrorModal
            title="Network Error"
            description="Failed to load the article, please check your internet
          connection and try again."
            onRetry={errorRetry.getContent}
          />
        )}

        <Container sx={{ pt: 5 }}>
          <MdViewer>{content}</MdViewer>
          {content && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                pb: 3,
                gap: "4px",
              }}
            >
              {prevArticle?.route && !prevArticle.noNav ? (
                <ArticleNavigator
                  link={prevArticle.route}
                  name={prevArticle.name}
                />
              ) : (
                <div />
              )}
              {nextArticle?.route ? (
                <ArticleNavigator
                  isNext
                  link={nextArticle.route}
                  name={nextArticle.name}
                />
              ) : (
                <div />
              )}
            </Box>
          )}
        </Container>
      </Box>
    </Slide>
  );
};

export default FocusTrap;
